import { VariantProps, cva } from 'class-variance-authority'
import { useEffect, useState } from 'react'

import { useSetOrgSignalVisibility, useSignalTypeTitles } from '@/api/signals.hook'
import { Alert } from '@/gen/inventory/v1/alert_pb'
import { CompanySummary } from '@/gen/inventory/v1/company_summary_pb'
import { SignalType } from '@/gen/inventory/v1/signal_pb'

import { useIsSupportUser } from '@/lib/app/support-user'
import { UrlParam, useUrlParams } from '@/lib/url/url-param.hook'

import { ExportSignal } from '@/pages/alert-details/export-signal'
import { FalsePositive } from '@/pages/alert-details/false-positive'

import { Badge } from '@/components/lemmala-design-system/badge/badge'
import { Button } from '@/components/lemmala-design-system/button/button'
import { DrawerLayoutHeader } from '@/components/lemmala-design-system/layouts/drawer-layout/drawer-layout-header'
import { CompanySummaryBadge } from '@/components/shared/company-summary-badge/company-summary-badge'
import { alertNameParts } from '@/components/shared/signal-type/column'

const signalDetailsHeaderTitleVariant = cva('min-w-0', {
  variants: {
    titleVariant: {
      default: 'line-clamp-1',
      showAll: '',
    },
  },
  defaultVariants: {
    titleVariant: 'default',
  },
})

export type SignalDetailsHeaderProps = {
  title: string
  subtitle: string
  id: string
  company?: CompanySummary
  visible?: boolean | null
  readOnly?: boolean
  hideSummary?: boolean
  alert?: Alert
  signalType?: SignalType
} & VariantProps<typeof signalDetailsHeaderTitleVariant>

export const SignalDetailsHeader = ({
  title,
  subtitle,
  id,
  company,
  visible,
  readOnly = false,
  titleVariant,
  hideSummary = false,
  alert,
  signalType,
}: SignalDetailsHeaderProps) => {
  const titleByType = useSignalTypeTitles()
  const resolvedType = signalType ?? alert?.signal?.signalType ?? SignalType.UNSPECIFIED
  const typeTitle = titleByType.get(resolvedType)
  // An alert is named by its type, as in the Alerts table, so the type badge would repeat it.
  const name = alert
    ? alertNameParts({ title, subtitle, signalType: resolvedType }, titleByType)
    : { title, description: subtitle }
  const typeBadge = alert ? undefined : typeTitle
  const isSupportUser = useIsSupportUser()
  const { mutateAsync: setVisibility } = useSetOrgSignalVisibility(id)
  const { removeParam } = useUrlParams()
  const [currentVisible, setCurrentVisible] = useState<boolean>(!!visible)

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setCurrentVisible(!!visible)
  }, [visible])

  return (
    <DrawerLayoutHeader
      badge={!hideSummary && company ? <CompanySummaryBadge companySummary={company} /> : undefined}
      actions={
        hideSummary ? undefined : (
          <>
            {isSupportUser && !readOnly && (
              <Button
                variant={'outline'}
                onClick={async () => {
                  const makeVisible = !currentVisible
                  await setVisibility({ id, visible: makeVisible })
                  setCurrentVisible(makeVisible)
                  if (!makeVisible) {
                    removeParam(UrlParam.SIGNAL_ID)
                  }
                }}
              >
                {currentVisible ? 'Hide signal' : 'Show signal'}
              </Button>
            )}
            {!readOnly && (
              <FalsePositive
                signalId={id}
                signalTitle={name.title}
                companyName={company?.profileName}
              />
            )}
            <ExportSignal signalId={id} alert={alert} />
          </>
        )
      }
      title={
        <span className='flex items-center gap-2'>
          <span className={signalDetailsHeaderTitleVariant({ titleVariant })}>{name.title}</span>
          {typeBadge ? (
            <Badge variant='neutral' badgeStyle='subtle' className='shrink-0'>
              {typeBadge}
            </Badge>
          ) : null}
        </span>
      }
      subtitle={
        name.description ? <p className='text-body-default'>{name.description}</p> : undefined
      }
    />
  )
}
